import React from "react";
import "./ClinicManagement.css";
import {
  CheckCircleFilled,
  ArrowRightOutlined,
  MedicineBoxOutlined,
  SafetyCertificateOutlined,
  CloudOutlined,
  RocketOutlined,
  CheckOutlined,
  ThunderboltFilled,
  UserOutlined,
  CalendarOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import clinicImage from "../../Asset/clinic-management.png";

const featuresLeft = [
  "Patient Registration & OPD Management",
  "Doctor Scheduling & Shift Management",
  "Appointment Booking & Token Generation",
  "Electronic Health Records (EHR)",
  "E-Prescriptions Generation",
  "IPD & Ward/Bed Management",
  "Pharmacy & Medicine Inventory Management",
  "Laboratory & Diagnostic Test Reports",
  "Patient Medical History Tracking",
  "Automated Appointment SMS & Email Alerts",
];

const featuresRight = [
  "Billing, Invoicing & GST Calculation",
  "Patient Portal & History Access",
  "Doctor & Staff Payroll Management",
  "Treatment & Care Plan Scheduling",
  "Expense & Financial Accounting",
  "Multi-Branch Clinic Management",
  "Secure Cloud Data Backup & Encryption",
  "Real-Time Analytical Dashboards",
  "Consent Form & Digital Signatures",
  "Variety of Diagnostic & Revenue Reports",
];

const highlights = [
  {
    icon: <UserOutlined />,
    title: "Patient Care Portal",
    desc: "Seamless OPD & digital EHR records",
  },
  {
    icon: <CalendarOutlined />,
    title: "Smart Scheduling",
    desc: "Doctor shifts & token generation",
  },
  {
    icon: <HeartOutlined />,
    title: "Clinical Excellence",
    desc: "E-prescriptions & diagnostics reports",
  },
];

const ClinicManagement = () => {
  const handleViewMore = () => {
    window.open("/clinic-details", "_blank");
  };

  return (
    <div className="cms-page-light">
      {/* Decorative Background Grid & Glows */}
      <div className="cms-bg-grid"></div>
      <div className="cms-glow-1"></div>
      <div className="cms-glow-2"></div>

      <div className="cms-container">
        
        {/* ═══════ PROFESSIONAL HEADER ═══════ */}
        <div className="cms-header-new">
          <div className="cms-header-top">
            <span className="cms-badge">
              <span className="cms-badge-dot"></span>
              HEALTHCARE PRODUCT
            </span>
            <span className="cms-version-tag">
              <ThunderboltFilled /> Version 4.2
            </span>
          </div>

          <div className="cms-header-main">
            <div className="cms-header-icon-box">
              <MedicineBoxOutlined />
            </div>
            <div className="cms-header-text">
              <h1 className="cms-title-new">
                Clinic Management <span className="cms-gradient-text">System (CMS)</span>
              </h1>
              <p className="cms-subtitle-new">
                An all-in-one clinical administration software designed to streamline healthcare workflows, 
                digitize electronic health records (EHR), track billing, manage pharmacies, and elevate the entire 
                patient care experience.
              </p>
            </div>
          </div>

          {/* Meta Info Pills */}
          <div className="cms-meta-pills">
            <span className="cms-meta-pill">
              <CheckOutlined /> Live Demo Available
            </span>
            <span className="cms-meta-pill">
              <SafetyCertificateOutlined /> HIPAA Compliant
            </span>
            <span className="cms-meta-pill">
              <CloudOutlined /> Encrypted Cloud Database
            </span>
            <span className="cms-meta-pill">
              <RocketOutlined /> 24/7 Support Included
            </span>
          </div>
        </div>

        {/* MAIN CONTENT GRID */}
        <div className="cms-main-content">
          
          {/* LEFT — IMAGE & HIGHLIGHTS */}
          <div className="cms-left-col">
            <div className="cms-image-wrapper">
              <div className="cms-image-glow"></div>
              <img
                src={clinicImage}
                alt="Clinic Management System"
                className="cms-image"
              />
              <div className="cms-image-badge">
                <MedicineBoxOutlined /> Clinic Dashboard
              </div>
            </div>

            {/* Highlights Row */}
            <div className="cms-highlights">
              {highlights.map((item, index) => (
                <div className="cms-highlight-card" key={index}>
                  <div className="cms-highlight-icon">{item.icon}</div>
                  <div className="cms-highlight-text">
                    <h5>{item.title}</h5>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — INFORMATION */}
          <div className="cms-right-col">
            <div className="cms-info-block">
              <p className="cms-para">
                A <strong>Clinic Management System (CMS)</strong> is an all-in-one medical 
                software engineered to simplify, streamline, and automate clinical, financial, 
                and administrative processes. It enables doctors, clinics, and multi-specialty hospitals 
                to manage operations smoothly and securely.
              </p>

              <p className="cms-para">
                By serving as a <strong>centralized, encrypted database solution</strong>, this application 
                ensures that doctor shift schedules, e-prescriptions, pharmacy medicine stocks, and lab diagnostics 
                reports can be securely accessed anytime, significantly reducing patient waiting times and manual errors.
              </p>
            </div>

            {/* FEATURES SECTION */}
            <div className="cms-features-section">
              <h2 className="cms-features-title">
                <span className="title-accent">Key Features</span> & Benefits
              </h2>
              <p className="cms-features-desc">
                Explore the robust parameters and administrative tools designed to modernize 
                your healthcare institute's daily patient processing workflows.
              </p>

              <div className="cms-features-grid">
                <ul className="cms-features-list">
                  {featuresLeft.map((feature, index) => (
                    <li key={index}>
                      <CheckCircleFilled className="cms-check-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <ul className="cms-features-list">
                  {featuresRight.map((feature, index) => (
                    <li key={index}>
                      <CheckCircleFilled className="cms-check-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTA BUTTON */}
            <div className="cms-cta-wrap">
              <button className="cms-view-btn" onClick={handleViewMore}>
                <span>Explore Full Details</span>
                <ArrowRightOutlined />
              </button>
              <p className="cms-cta-note">
                💡 Get a live demo and technical consultation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicManagement;